import React from "react";
import { Control, Controller, FieldErrors, FieldValues } from "react-hook-form";
import AppInput from "@src/components/AppInput";
import CustomAppInput from "@src/components/AppInput/AppInput.styles";
import DateRangePicker from "@src/components/DateRangePicker";
import FormToggleSwitch from "@src/components/FormToggleSwitch";
import { theme } from "@src/theme";
import { FEATURES } from "@src/constants";

type TracksFilterFieldsProps = {
  control: Control<FieldValues>;
  errors: FieldErrors;
  role?: string;
};

/**
 * Shared field set for the Library-style track filters. Rendered both by the
 * Library tab (`FilterTracksForm`) and the Mood +Add panel
 * (`MoodTracksFilterForm`) so the two stay in sync.
 */
const TracksFilterFields: React.FC<TracksFilterFieldsProps> = ({
  control,
  errors,
  role,
}) => {
  return (
    <>
      {role === "admin" && (
        <div style={{ marginBottom: theme.margins.medium }}>
          <FormToggleSwitch
            name="isTopLikedThisWeek"
            control={control}
            label="Top Liked This Week"
          />
        </div>
      )}

      {role === "admin" && (
        <div
          style={{
            marginBottom: theme.margins.medium,
            display: "flex",
            flexDirection: "row",
            alignItems: "flex-start",
            gap: "20px",
            flexWrap: "wrap",
          }}
        >
          <FormToggleSwitch
            name="missingYoutube"
            control={control}
            label="Missing YouTube"
          />
          <FormToggleSwitch
            name="missingSpotify"
            control={control}
            label="Missing Spotify"
          />
          <FormToggleSwitch
            name="missingApple"
            control={control}
            label="Missing Apple"
          />
          <FormToggleSwitch
            name="missingShazam"
            control={control}
            label="Missing Shazam"
          />
        </div>
      )}

      {role === "admin" && (
        <div style={{ marginBottom: theme.margins.medium }}>
          <Controller
            name="createdDateRange"
            control={control}
            render={({ field }) => (
              <DateRangePicker
                label="Created Date Range"
                name="createdDateRange"
                control={control}
                errors={errors}
                value={field.value}
                onDateChange={(selection) => {
                  field.onChange({
                    startDate: selection.startDate?.toISOString(),
                    endDate: selection.endDate?.toISOString(),
                  });
                }}
              />
            )}
          />
        </div>
      )}

      {role === "admin" && FEATURES.LIBRARY_SCHEDULE_DATE_FILTER_ENABLED && (
        <div style={{ marginBottom: theme.margins.medium }}>
          <Controller
            name="scheduleDateRange"
            control={control}
            render={({ field }) => (
              <DateRangePicker
                label="Scheduled Date Range"
                name="scheduleDateRange"
                control={control}
                errors={errors}
                value={field.value}
                onDateChange={(selection) => {
                  field.onChange({
                    startDate: selection.startDate?.toISOString(),
                    endDate: selection.endDate?.toISOString(),
                  });
                }}
              />
            )}
          />
        </div>
      )}

      <div style={{ marginBottom: theme.margins.medium }}>
        <Controller
          name="title"
          control={control}
          render={({ field }) => (
            <AppInput
              label={"Title"}
              control={control}
              errors={errors}
              {...field}
              placeholder="Keyword(s)"
            />
          )}
        />
      </div>

      <div style={{ marginBottom: theme.margins.medium }}>
        <Controller
          name="artist"
          control={control}
          render={({ field }) => (
            <AppInput
              label={"Artist"}
              control={control}
              errors={errors}
              {...field}
              placeholder="Keyword(s)"
            />
          )}
        />
      </div>

      <div style={{ marginBottom: theme.margins.medium }}>
        <Controller
          name="album"
          control={control}
          render={({ field }) => (
            <AppInput
              label={"Album"}
              control={control}
              errors={errors}
              {...field}
              placeholder="Keyword(s)"
            />
          )}
        />
      </div>

      <div style={{ marginBottom: theme.margins.medium }}>
        <Controller
          name="label"
          control={control}
          render={({ field }) => (
            <AppInput
              label={"Label"}
              control={control}
              errors={errors}
              {...field}
              placeholder="Keyword(s)"
            />
          )}
        />
      </div>

      <div style={{ marginBottom: theme.margins.medium }}>
        <CustomAppInput
          label={"BPM"}
          name="bpm"
          control={control}
          errors={errors}
          placeholder="BPM"
          type="range"
          displayInput
          min={0}
          max={255}
        />
      </div>

      <div style={{ marginBottom: theme.margins.medium }}>
        <Controller
          name="key"
          control={control}
          render={({ field }) => (
            <AppInput
              label={"Key"}
              control={control}
              errors={errors}
              {...field}
              placeholder="Keyword(s)"
            />
          )}
        />
      </div>

      <div style={{ marginBottom: theme.margins.medium }}>
        <CustomAppInput
          label={"Duration (minutes)"}
          name="durationInMinutes"
          control={control}
          errors={errors}
          placeholder="Duration"
          type="range"
          displayInput
          min={0}
          max={160}
        />
      </div>
    </>
  );
};

export default TracksFilterFields;
